/**
 * IndependentTrackBanner — Track C (independent pupil) explicit UX strip.
 *
 * Operator brief (Feb 13, 2026 — Phase 2 frontend integration):
 *   "AI guidance, not instructor-confirmed"
 *   "Use this to understand your progress, practise smarter, and decide
 *    what to work on next."
 *   "For professional confirmation, connect with an instructor."
 *
 * Rules:
 *   • Visible ONLY when /api/tracks/me returns `is_independent === true`.
 *   • Calm, informational, NEVER negative. Never implies AI replaces an ADI.
 *   • Marketplace / Find-Instructor CTA routes to the existing
 *     `/learn/connect` connection-request page.
 *   • No payment / subscription change wired into this banner.
 *
 * Designed to mount near the top of /learn so independent pupils
 * immediately understand the framing of their experience.
 */
import { Compass, ArrowRight, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";


export default function IndependentTrackBanner() {
  return (
    <section
      data-testid="independent-track-banner"
      className="rounded-2xl border border-indigo-700/40 bg-gradient-to-br from-indigo-900/30 to-slate-900/30 p-4 sm:p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-indigo-600/30 border border-indigo-600/40 flex items-center justify-center">
          <Compass className="w-3.5 h-3.5 text-indigo-300" />
        </div>
        <p
          data-testid="independent-track-badge"
          className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300"
        >
          Independent learning · AI guidance, not instructor-confirmed
        </p>
      </div>
      <p
        data-testid="independent-track-purpose"
        className="text-sm text-white leading-relaxed font-semibold"
      >
        Use this to understand your progress, practise smarter, and decide what
        to work on next.
      </p>
      <p
        data-testid="independent-track-recommendation"
        className="text-[12px] text-indigo-100/80 leading-relaxed"
      >
        For professional confirmation, connect with an instructor.
      </p>
      <Link
        to="/learn/connect"
        data-testid="independent-find-instructor-cta"
        className="mt-1 inline-flex items-center justify-center gap-2 w-full rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-extrabold py-2.5 active:scale-[0.99] transition"
      >
        <UserPlus className="w-4 h-4" />
        Find or connect with an instructor
        <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
}
